/**
 * L'editor delle opzioni (CF05) — CL02.
 *
 * Presentazione pura: nessuna regola vive qui. Le mutazioni stanno in
 * `choiceOptions.ts`, i problemi in `optionIssues`, e questo file si limita a
 * mostrarli. La ragione e' che le regole vanno verificate senza un browser, e un
 * componente che le contiene non si lascia verificare.
 *
 * La label e' l'unica cosa modificabile a mano libera: l'id no, ed e' la
 * ragione per cui non compare da nessuna parte nella UI. Un id e' un indirizzo,
 * non un'informazione per chi progetta. L'eccezione e' la modalità «Chiave e
 * valore» della scelta multipla, dove il progettista scrive la chiave (l'id) e
 * il valore (la label) di ogni voce.
 */

import {
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
} from "@mui/material";
import { Add, Delete, DragIndicator } from "@mui/icons-material";
import React from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import type { OptionDefinition } from "model/CustomFields";
import { getTranslation } from "common";
import { useTranslations } from "hooks";
import FlowentiButton from "components/flowenti-ui-kit/FlowentiButton";
import {
  addOption,
  canAddOption,
  moveOptionFromTo,
  optionIssues,
  removeOption,
  renameOption,
} from "./choiceOptions";

interface ChoiceConfigurationProps {
  options: readonly OptionDefinition[];
  /**
   * Riceve l'elenco nuovo. Non si muta quello ricevuto: e' la stessa
   * disciplina della bozza, e serve perche' annullare funzioni.
   */
  onChange: (options: OptionDefinition[]) => void;
  disabled?: boolean;
  showErrors?: boolean;
  /**
   * Modalità «Dinamico»: ogni voce porta una **chiave** scelta a mano (l'`id`)
   * oltre al valore mostrato (la `label`).
   */
  dynamic?: boolean;
  onDynamicChange?: (dynamic: boolean) => void;
  /** Solo la scelta multipla offre il flag. */
  allowDynamic?: boolean;
}

const ChoiceConfiguration: React.FC<ChoiceConfigurationProps> = ({
  options,
  onChange,
  disabled = false,
  showErrors = true,
  dynamic = false,
  onDynamicChange,
  allowDynamic = false,
}) => {
  const translations = useTranslations();
  const issues = optionIssues(options);
  const issueFor = (id: string) =>
    issues.find((issue) => issue.id === id)?.issue;

  /** Il testo di una voce del dizionario, o il ripiego scritto nel codice. */
  const testo = (key: string, fallback: string) => {
    const risolta = getTranslation(translations, key);
    return !risolta || risolta === key ? fallback : risolta;
  };

  /**
   * Il problema della chiave, in modalità dinamica.
   *
   * Vuota e duplicata sono due cose diverse: la prima il server la rifiuta
   * come campo mancante, la seconda come chiave già usata. Il controllo di
   * salvataggio le conosce già (`choiceConfigIssues`); qui si dice **quale**
   * riga, come per la label.
   */
  const keyIssueFor = (id: string): string | undefined => {
    if (!dynamic) return undefined;
    const trimmed = id.trim();
    if (trimmed === "") return "custom_fields.error.validation_failed";
    return options.filter((option) => option.id.trim() === trimmed).length > 1
      ? "custom_fields.error.duplicate_option"
      : undefined;
  };

  /**
   * La riga rilasciata si cerca per `draggableId` — che e' l'id dell'opzione —
   * e non per indice: nel frattempo l'indice e' cambiato sotto. Le frecce sono
   * state tolte in favore di questa: due modi di riordinare lo stesso elenco
   * sono due cose da mantenere, e il trascinamento e' quello che si aspetta.
   */
  const onDragEnd = ({ source, destination }: {
    source: { index: number };
    destination: { index: number } | null | undefined;
  }) => {
    if (disabled) return;
    if (destination && destination.index !== source.index) {
      // Per posizione: una riga dinamica può avere la chiave ancora vuota, e
      // cercarla per id confonderebbe fra loro le righe non compilate.
      onChange(moveOptionFromTo(options, source.index, destination.index));
    }
  };

  return (
    <Stack gap={1} data-testid="cf-options">
      {/* Il flag è della scelta multipla: acceso, ogni voce chiede chiave e
          valore; spento, la chiave resta l'id generato e si scrive solo il
          valore. */}
      {allowDynamic && (
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={dynamic}
              disabled={disabled}
              inputProps={
                { "data-testid": "cf-option-dynamic" } as React.InputHTMLAttributes<HTMLInputElement>
              }
              onChange={(e, checked) => onDynamicChange?.(checked)}
            />
          }
          label={testo("custom_fields.config.options.dynamic.label", "Chiave e valore")}
        />
      )}
      {/* Le righe sono un'area di rilascio a sé: il segnaposto che rbd infila
          durante il trascinamento muove solo loro, e il pulsante in fondo
          (aggiungi) resta dove sta. */}
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="cf-choice-options">
          {(provided) => (
            <Stack
              gap={1}
              ref={provided.innerRef}
              {...provided.droppableProps}
            >
              {options.map((option, index) => {
                const issue = showErrors ? issueFor(option.id) : undefined;
                const keyIssue = showErrors ? keyIssueFor(option.id) : undefined;
                // In modalità dinamica l'identità stabile è la **posizione**:
                // l'id è ciò che l'utente sta scrivendo, e usarlo come key
                // rimonterebbe l'input a ogni tasto (focus perso).
                const rowKey = dynamic ? `opt-${index}` : option.id;

                return (
                  <Draggable
                    key={rowKey}
                    draggableId={rowKey}
                    index={index}
                    isDragDisabled={disabled}
                  >
                    {(drag) => (
                      <Stack
                        ref={drag.innerRef}
                        {...drag.draggableProps}
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        data-testid={`cf-option-${rowKey}`}
                      >
                        {/* La maniglia è l'unico punto che trascina: senza, un
                            clic dentro un campo di testo sposterebbe la riga
                            mentre si scrive. */}
                        <Box
                          {...drag.dragHandleProps}
                          data-testid={`cf-option-handle-${rowKey}`}
                          sx={{
                            display: "flex",
                            cursor: disabled ? "default" : "grab",
                            color: "text.disabled",
                            "&:hover": { color: disabled ? "text.disabled" : "text.secondary" },
                            "&:active": { cursor: disabled ? "default" : "grabbing" },
                          }}
                        >
                          <DragIndicator fontSize="small" />
                        </Box>
                        {dynamic && (
                          <TextField
                            size="small"
                            sx={{ flex: 1 }}
                            inputProps={{ "data-testid": `cf-option-key-${rowKey}` }}
                            label={testo("custom_fields.config.options.key.label", "Chiave")}
                            value={option.id}
                            disabled={disabled}
                            // La chiave è l'indirizzo del valore: vuota o
                            // doppia il server la rifiuta, e qui si dice quale
                            // riga la causa.
                            error={Boolean(keyIssue)}
                            helperText={
                              keyIssue ? getTranslation(translations, keyIssue) : undefined
                            }
                            onKeyUp={(e) => e.stopPropagation()}
                            onChange={(e) =>
                              onChange(
                                options.map((other, i) =>
                                  i === index ? { ...other, id: e.target.value } : other
                                )
                              )
                            }
                          />
                        )}
                        <TextField
                          size="small"
                          sx={{ flex: 1 }}
                          inputProps={{ "data-testid": `cf-option-label-${rowKey}` }}
                          label={
                            dynamic
                              ? testo("custom_fields.config.options.value.label", "Valore")
                              : getTranslation(
                                  translations,
                                  "custom_fields.config.options.option.label"
                                )
                          }
                          value={option.label}
                          disabled={disabled}
                          // L'errore si mostra accanto alla riga che lo causa: un
                          // elenco di problemi in fondo lascerebbe cercare quale
                          // voce li ha.
                          error={Boolean(issue)}
                          helperText={issue ? getTranslation(translations, issue) : undefined}
                          onKeyUp={(e) => e.stopPropagation()}
                          onChange={(e) =>
                            onChange(
                              dynamic
                                ? options.map((other, i) =>
                                    i === index ? { ...other, label: e.target.value } : other
                                  )
                                : renameOption(options, option.id, e.target.value)
                            )
                          }
                        />
                        <IconButton
                          size="small"
                          data-testid={`cf-option-delete-${rowKey}`}
                          disabled={disabled}
                          onClick={() =>
                            onChange(
                              dynamic
                                ? options.filter((_, i) => i !== index)
                                : removeOption(options, option.id)
                            )
                          }
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Stack>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </Stack>
          )}
        </Droppable>
      </DragDropContext>

      <FlowentiButton
        data-testid="cf-option-add"
        // Il limite e' del contratto, non della UI: il pulsante lo rispetta
        // invece di lasciar aggiungere una voce che il server rifiuterebbe.
        disabled={disabled || !canAddOption(options)}
        onClick={() => onChange(addOption(options, dynamic))}
        startIcon={<Add />}
        size="small"
        variant="outlined"
        sx={{ alignSelf: "flex-start" }}
      >
        {getTranslation(translations, "custom_fields.config.options.add")}
      </FlowentiButton>
    </Stack>
  );
};

export default ChoiceConfiguration;
